import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const initial = { name: "", email: "", organization: "", user_type: "", message: "", company_website: "" };

export default function LeadForm({ dark = false }) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Enter a valid work email.";
    if (form.organization.trim().length < 2) e.organization = "Tell us your organization.";
    if (!form.user_type) e.user_type = "Select who you are.";
    if (form.message.trim().length < 5) e.message = "Add a short message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await api.post("/leads", form);
      setDone(true);
      toast.success(res.data?.message || "Request received. We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const labelCls = `block text-sm font-medium mb-1.5 ${dark ? "text-zinc-200" : "text-zinc-700"}`;
  const inputCls = `w-full px-4 py-2.5 rounded-sm text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#0033A0] ${
    dark
      ? "bg-zinc-800 border border-zinc-700 text-white placeholder:text-zinc-500"
      : "bg-white border border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
  }`;

  if (done) {
    return (
      <div
        data-testid="lead-form-success"
        className={`rounded-lg p-10 text-center ${dark ? "bg-zinc-800/60" : "bg-white border border-zinc-200"}`}
      >
        <CheckCircle2 className="mx-auto text-[#16A34A]" size={48} strokeWidth={1.5} />
        <h3 className={`mt-4 font-heading text-2xl font-medium ${dark ? "text-white" : "text-zinc-900"}`}>
          Request received
        </h3>
        <p className={`mt-2 text-sm ${dark ? "text-zinc-400" : "text-zinc-600"}`}>
          Thanks for reaching out. A WebADAC specialist will contact you shortly to schedule your demo.
        </p>
        <button
          data-testid="lead-form-reset-button"
          onClick={() => setDone(false)}
          className="mt-6 text-sm font-medium text-[#0033A0] hover:underline"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form data-testid="lead-form" onSubmit={submit} className="space-y-5" noValidate>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="name" className={labelCls}>Full name</label>
          <input
            id="name"
            data-testid="lead-name-input"
            value={form.name}
            onChange={update("name")}
            placeholder="Jane Doe"
            className={inputCls}
          />
          {errors.name && <p data-testid="lead-name-error" className="mt-1 text-xs text-red-500">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="email" className={labelCls}>Work email</label>
          <input
            id="email"
            type="email"
            data-testid="lead-email-input"
            value={form.email}
            onChange={update("email")}
            placeholder="jane@council.gov.uk"
            className={inputCls}
          />
          {errors.email && <p data-testid="lead-email-error" className="mt-1 text-xs text-red-500">{errors.email}</p>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="organization" className={labelCls}>Organization</label>
          <input
            id="organization"
            data-testid="lead-organization-input"
            value={form.organization}
            onChange={update("organization")}
            placeholder="City Council / Consultancy"
            className={inputCls}
          />
          {errors.organization && (
            <p data-testid="lead-organization-error" className="mt-1 text-xs text-red-500">{errors.organization}</p>
          )}
        </div>
        <div>
          <label htmlFor="user_type" className={labelCls}>I am a…</label>
          <select
            id="user_type"
            data-testid="lead-usertype-select"
            value={form.user_type}
            onChange={update("user_type")}
            className={inputCls}
          >
            <option value="">Select…</option>
            <option value="council">Council / Reviewer</option>
            <option value="consultant">Consultant / Producer</option>
            <option value="other">Other</option>
          </select>
          {errors.user_type && (
            <p data-testid="lead-usertype-error" className="mt-1 text-xs text-red-500">{errors.user_type}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="message" className={labelCls}>How can we help?</label>
        <textarea
          id="message"
          data-testid="lead-message-input"
          value={form.message}
          onChange={update("message")}
          rows={4}
          placeholder="Tell us about your ADAC workflow and what you'd like to see."
          className={inputCls + " resize-none"}
        />
        {errors.message && <p data-testid="lead-message-error" className="mt-1 text-xs text-red-500">{errors.message}</p>}
      </div>

      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
        <label>
          Company website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.company_website}
            onChange={update("company_website")}
          />
        </label>
      </div>

      <button
        type="submit"
        data-testid="lead-submit-button"
        disabled={loading}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0033A0] text-white hover:bg-[#00257A] transition-colors px-7 py-3 text-sm font-medium rounded-sm disabled:opacity-60"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
        {loading ? "Sending…" : "Book my demo"}
      </button>
    </form>
  );
}
